import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from '../icons/X';
import {
  Button,
  ButtonProps,
  TEXT_BUTTON_SIZE,
  TEXT_BUTTON_TYPE
} from '../Button';
import { cn } from '../../utils/cn';

type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

interface DialogContextValue {
  disabled?: boolean;
}

const DialogContext = React.createContext<DialogContextValue>({});

const useDialogContext = () => React.useContext(DialogContext);

const dialogSizeStyles: Record<DialogSize, string> = {
  sm: 'max-w-[402px]',
  md: 'max-w-[522px]',
  lg: 'max-w-[642px]',
  xl: 'max-w-[1002px]',
};

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-[var(--bg-overlay-dim-overlay,rgba(0,0,0,0.65))] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  size?: DialogSize;
  disabled?: boolean;
}
>(({ className, children, size = 'md', disabled, ...props }, ref) => (
  <DialogContext.Provider value={{ disabled }}>
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => disabled && e.preventDefault()}
        ref={ref}
        className={cn(
          'bg-white border border-secondary rounded-m',
          "fixed left-[50%] top-[50%] z-50 flex flex-col w-full max-h-[calc(100vh-128px)] translate-x-[-50%] translate-y-[-50%] shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] overflow-hidden",
          dialogSizeStyles[size],
          className
        )}
        {...props}>
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  </DialogContext.Provider>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const { disabled } = useDialogContext();

  return (
    <div
      className={cn(
        'flex items-center justify-between border-b border-secondary py-l pl-3xl pr-9xl',
        className
      )}
      {...props}>
      <div className="flex-1">{children}</div>
      <DialogPrimitive.Close asChild disabled={disabled}>
        <Button
          variant={TEXT_BUTTON_TYPE.TERTIARY_GRAY}
          size={TEXT_BUTTON_SIZE.S}
          iconOnly
          aria-label="Close"
          className="absolute right-l top-l"
          disabled={disabled}
        >
          <XIcon />
        </Button>
      </DialogPrimitive.Close>
    </div>
  );
};
DialogHeader.displayName = 'DialogHeader';

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    asChild
    className={cn('p-3xl overflow-y-auto', className)}
    {...props}
  >
    <div>{props.children}</div>
  </DialogPrimitive.Description>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'h-[68px] min-h-[68px] flex items-center justify-end gap-2xs px-3xl py-l border-t border-secondary',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'py-3xs flex-1 text-primary text-xl font-semibold not-italic leading-[24px]',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogAction = React.forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ className, variant = TEXT_BUTTON_TYPE.PRIMARY, size = TEXT_BUTTON_SIZE.S, ...props }, ref) => (
  <Button
    ref={ref}
    variant={variant}
    size={size}
    className={className}
    {...props}
  />
));
DialogAction.displayName = 'DialogAction';

const DialogCancel = React.forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ className, variant = TEXT_BUTTON_TYPE.SECONDARY_GRAY, size = TEXT_BUTTON_SIZE.S, disabled: disabledProp, ...props }, ref) => {
  const { disabled: contextDisabled } = useDialogContext();
  const disabled = disabledProp ?? contextDisabled;

  return (
    <DialogPrimitive.Close asChild disabled={disabled}>
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={className}
        disabled={disabled}
        {...props}
      />
    </DialogPrimitive.Close>
  );
});
DialogCancel.displayName = 'DialogCancel';

export {
  Dialog,
  DialogOverlay,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogDescription as DialogBody,
  DialogFooter,
  DialogTitle,
  DialogAction,
  DialogCancel,
};
